import CheckIcon from "assets/icons/CheckIcon";
import { MouseEvent, useLayoutEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";

import styles from "./style.module.scss";
import { VLInput } from "../../../../uikit/FormFields/VLInput/VLInput";

interface SingleOptionProps {
  isSelected: boolean;
  showRadioButton: boolean;
  option: { id: string; text: string };
  handleSelection: (option: string) => void;
}

const SingleOption = ({
  showRadioButton,
  handleSelection,
  isSelected,
  option,
}: SingleOptionProps) => {
  const filterClassnames = classNames(styles.optionContainer, {
    [styles.selectedFilter]: isSelected,
  });

  const checkboxClassnames = classNames(
    showRadioButton ? styles.radioButton : styles.checkbox,
    {
      [styles.blueBackground]: isSelected,
    },
  );

  const icon = showRadioButton ? (
    <div className={styles.radioButtonIcon} />
  ) : (
    <CheckIcon variant="flat" />
  );

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
    handleSelection(option.id);
  };
  return (
    <div onClick={handleClick} className={filterClassnames}>
      <div className={checkboxClassnames}>{isSelected && icon}</div>
      <div
        className={styles.text}
        data-testid={"dataset-inventory-filter-option"}
      >
        {option.text}
      </div>
    </div>
  );
};

interface FilterDropdownProps {
  selectedOptions: string[];
  allowMultiSelect?: boolean;
  selectedOption: string | null;
  defaultOptionText?: "All";
  options: { id: any; text: string }[];
  handleSelection: (id: string | null) => void;
  showSearch?: boolean;
}

const FilterDropdown = ({
  options,
  selectedOption,
  selectedOptions,
  handleSelection,
  allowMultiSelect,
  defaultOptionText,
  showSearch,
}: FilterDropdownProps) => {
  const inputRef = useRef<any>(null);
  const [search, setSearch] = useState("");

  useLayoutEffect(() => {
    if (showSearch) {
      inputRef.current?.focus();
    }
  }, [showSearch]);

  const displayedOptions = useMemo(() => {
    if (!search) {
      return options;
    }
    return options.filter((option) =>
      option.text.toLowerCase().includes(search.toLowerCase()),
    );
  }, [options, search]);

  const checkOptionSelected = (id: string) => {
    if (allowMultiSelect) {
      return selectedOptions?.includes(id) || false;
    } else {
      return selectedOption === id;
    }
  };

  const handleDefaultSelection = (e: MouseEvent) => {
    e.stopPropagation();
    handleSelection(defaultOptionText!);
  };

  const renderDefaultOption = () => {
    if (!defaultOptionText) {
      return <noscript />;
    }

    const isSelected = selectedOptions.length === options.length;
    const defaultOptionClassnames = classNames(styles.optionContainer, {
      [styles.selectedFilter]: isSelected,
    });

    return (
      <div className={defaultOptionClassnames} onClick={handleDefaultSelection}>
        <div
          className={classNames(styles.checkbox, {
            [styles.blueBackground]: isSelected,
          })}
        >
          {isSelected && <CheckIcon variant="flat" />}
        </div>
        <div className={styles.text}>{defaultOptionText}</div>
      </div>
    );
  };

  return (
    <div className={styles.dropdown}>
      {showSearch && (
        <div className={styles.searchWrapper}>
          <VLInput
            ref={inputRef}
            placeholder="Search value..."
            className={styles.search}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}
      {search && !displayedOptions.length && (
        <div className={classNames(styles.text, styles.noResults)}>
          No results
        </div>
      )}
      {displayedOptions.map((option, index) => (
        <SingleOption
          key={option.text + index}
          option={option}
          handleSelection={handleSelection}
          isSelected={checkOptionSelected(option.id)}
          showRadioButton={!allowMultiSelect}
        />
      ))}

      {defaultOptionText && (
        <>
          <div className={styles.divider} />
          {renderDefaultOption()}
        </>
      )}
    </div>
  );
};

export default FilterDropdown;
